// Product types for customer frontend
export enum ProductType {
  NORMAL = 'normal',
  EVENT_PACKAGE = 'event_package',
  FOOD = 'food',
  SUBSCRIPTION = 'subscription',
  SERVICE = 'service',
  DIGITAL = 'digital',
  PHYSICAL_GOODS = 'physical_goods',
  MEMBERSHIP = 'membership',
  BUNDLE = 'bundle',
}

export enum ProductStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface Product {
  id: number;
  name: string;
  slug?: string;
  description: string;
  descriptionBoxes?: string[];
  price: number;
  originalPrice?: number;
  compareAtPrice?: number;  // Compare at price
  discountPercentage?: number;
  productType: ProductType;
  type?: ProductType;  // Alias for productType
  category?: string;
  images?: string[];
  imageUrl?: string | string[];  // Single image or array
  attributes?: ProductAttributes;
  tenantId: number;
  status: ProductStatus;
  isActive?: boolean;  // Convenience property
  isFeatured?: boolean;  // Featured product flag
  sku?: string;        // Product SKU
  stock?: number;      // Stock quantity
  createdAt: string;
  updatedAt: string;
  weight?: number;     // Weight in kg
  options?: any[];     // Product options/add-ons (size, color, etc.)
  isDemo?: boolean;    // True for seed/demo products. ProductsSection swaps to template defaults when every product is demo.
}

export interface ProductAttributes {
  descriptionBoxes?: string[];
  catering?: CateringAttributes;
  food?: FoodAttributes;
  subscription?: SubscriptionAttributes;
  service?: ServiceAttributes;
  digital?: DigitalAttributes;
  physicalGoods?: PhysicalGoodsAttributes;
  physical?: PhysicalGoodsAttributes;  // Alias for physicalGoods
  membership?: MembershipAttributes;
  bundle?: BundleAttributes;
}

// Event Package / Catering Attributes
export interface CateringAttributes {
  paxConfig?: {
    minPax?: number;
    maxPax?: number;
  };
  advanceNoticeDays?: number;
  venueCapacity?: number;
  requiresEventDate?: boolean;
  eventDate?: string;
  venueType?: string;
  setupTime?: number;
}

// Food Attributes
export interface FoodAttributes {
  servingSize?: string;
  calories?: number;
  ingredients?: string[];
  allergens?: string[];
  spicyLevel?: 'mild' | 'medium' | 'hot' | 'extra-hot';
  preparationTime?: number;
  cuisine?: string;
  dietaryInfo?: string[];
}

// Subscription Attributes
export interface SubscriptionAttributes {
  billingCycle?: 'monthly' | 'quarterly' | 'yearly';
  trialDays?: number;
  trialPrice?: number;
  features?: string[];
  minimumTerm?: number;
  autoRenew?: boolean;
  cancellationPolicy?: string;
}

// Service Attributes
export interface ServiceAttributes {
  duration?: number; // in minutes
  bookingWindow?: number; // days in advance
  maxParticipants?: number;
  includesEquipment?: boolean;
  location?: string;
  cancellationWindow?: number; // hours before
}

// Digital Product Attributes
export interface DigitalAttributes {
  fileType?: string;
  fileFormat?: string;  // Alias for fileType
  fileSize?: number; // in bytes
  accessType?: 'download' | 'streaming' | 'license';
  licenseType?: string;  // License type info
  accessDuration?: number; // days (0 = lifetime)
  downloadLimit?: number;
  platform?: string[];
  requirements?: string[];  // System requirements
}

// Physical Goods Attributes
export interface PhysicalGoodsAttributes {
  weight?: number | { value: number; unit: string }; // in kg or structured
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit?: string;
  };
  requiresShipping?: boolean;
  shippingDays?: number;
  shippingTime?: number;  // Alias for shippingDays
  stockQuantity?: number;
  sku?: string;
  brand?: string;
  material?: string;  // Product material
  colors?: string[];  // Available colors
}

// Membership Attributes
export interface MembershipAttributes {
  membershipLevel?: 'basic' | 'premium' | 'vip' | 'enterprise';
  tier?: string;  // Membership tier
  duration?: number; // in days
  validityPeriod?: { value: number; unit: string };  // Structured validity
  benefits?: string[];
  maxMembers?: number;
  memberCount?: number;  // Current member count
  transferable?: boolean;
  autoRenew?: boolean;
  earlyBirdDiscount?: number;
  exclusiveAccess?: string[];  // Exclusive features
}

// Bundle Attributes
export interface BundleAttributes {
  includedProducts?: number[]; // product IDs
  includedItems?: Array<{ name: string; value: number; quantity?: number }>;  // Structured items
  discountType?: 'percentage' | 'fixed';
  discountValue?: number;
  bundleDescription?: string;
  validUntil?: string;
  minQuantity?: number;
  customizable?: boolean;  // Can be customized
}

// API Response Types
export interface ProductListResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProductFilters {
  productType?: ProductType;
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
  minPrice?: number;
  maxPrice?: number;
}
